import {
  Grid,
  TextField,
  Typography,
  Box,
  Divider,
  Stack,
  IconButton,
  Button,
  Dialog,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Class, CreateUserRequestBody, DbUser, School } from "../../../../interfaces";
import { ClassSelect } from "../utils/ClassSelect";
import { Role, SubRoles } from "../../../../enums";
import { RoleSelect } from "../utils/RoleSelect";
import { SchoolSelect } from "../utils/SchoolSelect";
import { FeatureFlagSelect } from "../utils/FeatureFlagSelect";
import { TeacherSelect } from "../utils/TeacherSelect";
import { UserManagementModal } from "../UserManagementModal";
import { GradeSelect } from "../utils/GradeSelect";
import { AlertState } from "../../../../types";
import { DigitSupportLink, TechnicalIssuesMessage } from "../../utils/Toast";
import axios from "../../../../services/axios";
import { makeStyles } from "@mui/styles";
import { v2theme } from "../../../../styles/Theme";
import { CustomCheckBox } from "../../utils/CustomCheckBox";
import { generateGtmEvent } from "../../../../Helpers";
import { FeatureFlag, ViewUserManagement } from "@backend-types";
import { logger } from "../../../../services/logger";
import { AxiosError } from "axios";

export type EditUserV2Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (alert: AlertState) => void;
  currentUser: DbUser;
  user?: ViewUserManagement;
};

const useStyle = makeStyles((theme) => ({
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
  deleteClassRelButton: {
    color: theme.palette.text.secondary,
    fontSize: "1em",
    width: "30px",
    height: "30px",
    marginLeft: "24px",
  },
  addCourseButton: {
    height: "32px",
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "19px",
  },
  dialogRoot: {
    backgroundColor: "transparent",
  },
  newPosOfDialog: {
    padding: 32,
    maxWidth: 500,
    minHeight: 171,
    backgroundColor: "#FFFFFF",
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 15,
  },
  dialogText: {
    marginBottom: "33px",
  },

  buttonNo: {
    width: 210,
    padding: 0,
    backgroundColor: "#D8D8D8",
    color: theme.palette.text.primary,
    "&:hover": {
      backgroundColor: "#C9C8C8",
    },
  },
  buttonYes: { width: 210, padding: 0 },
}));

export interface StudentClassRelation {
  teacherId: string | null;
  classId: number | null;
}

interface TeacherDetails {
  teacher: DbUser | null;
  classes: Class[];
}

type TeacherClassMap = Map<string | "no-teacher-assigned", TeacherDetails>;

export function EditUserV2(props: EditUserV2Props) {
  const classes = useStyle();
  const [promptOpen, setPromptOpen] = useState<{
    index: number;
    className: string;
  } | null>(null);
  const [selectedUser, setSelectedUser] = useState<DbUser | null>(null);
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [role, setRole] = useState<Role>(Role.student);
  const [schoolId, setSchoolId] = useState<number | undefined | null>(undefined);
  const [grade, setGrade] = useState<number | null>(null);
  const [isTester, setIsTester] = useState<boolean>(false);
  const [isReadOnlyAdmin, setIsReadOnlyAdmin] = useState<boolean>(false);

  const [accessibility, setAccessibility] = useState<boolean>(false);
  const [studentClasses, setStudentClasses] = useState<StudentClassRelation[]>([]);
  const [featureIds, setFeatureIds] = useState<string[]>([]);
  // dropdown lists
  const [schoolList, setSchoolList] = useState<School[]>([]);
  const [featureFlagList, setFeatureFlagList] = useState<FeatureFlag[]>([]);
  const [teacherClasses, setTeacherClasses] = useState<TeacherClassMap>(
    new Map<string, TeacherDetails>()
  );

  let display = !(
    props.currentUser?.role === Role.admin || props.currentUser?.role === Role.super
  );
  let displayEmail =
    props.currentUser?.role !== Role.teacher ||
    (props.currentUser?.role === Role.teacher &&
      props.user?.teacher_id.includes(props.currentUser.teacher_id ?? ""));

  const displaySubRole = props.currentUser?.role === Role.super;

  useEffect(() => {
    if (!props.open) return;
    // Wait to get teachers until school is chosen
    if (!(schoolId && [Role.super, Role.admin].includes(props.currentUser.role))) return;
    const getTeachers = async () => {
      try {
        const res = await axios.get<DbUser[]>("/teachers", {
          params: { schoolId: schoolId },
        });
        // itterate through teachers and slot them into the dropdown Map
        const newTeacherClasses: TeacherClassMap = new Map<string, TeacherDetails>();
        res.data.forEach((item) => {
          const teacherId = item.id;
          // push teacher into teacher list
          // push their class into their respective map item

          const curTeacherClass: TeacherDetails = newTeacherClasses.get(teacherId) ?? {
            teacher: item as DbUser,
            classes: [],
          };
          curTeacherClass.classes.push(...item.class);
          newTeacherClasses.set(teacherId, curTeacherClass);
        });

        // pre-populate the class/student dropdowns with the selected items from selected user
        // handles for classes that don't appear at the student's school in case of LTI mismatch
        selectedUser?.class_student?.forEach((item) => {
          const teacherId = item.class?.user?.id ?? "no-teacher-assigned";
          if (item.class) {
            // push teacher into teacher list
            // push their class into their respective map item
            const curTeacherClass: TeacherDetails = newTeacherClasses.get(teacherId) ?? {
              teacher: (item.class.user as DbUser) ?? null,
              classes: [],
            };
            const classIds = curTeacherClass.classes.map((classe) => classe.id);
            if (!classIds.includes(item.class.id)) {
              curTeacherClass.classes.push(item.class);
            }
            newTeacherClasses.set(teacherId, curTeacherClass);
          }
        });
        // update teacher names to indicate school mismatches for LTI/school desyncs
        newTeacherClasses.forEach((value, key) => {
          const teacherSchool =
            value.teacher?.user_school && value.teacher.user_school.length > 0
              ? value.teacher.user_school[0]
              : null;
          const teacherSchoolId = teacherSchool?.school_id ?? null;

          if (teacherSchoolId !== schoolId) {
            newTeacherClasses.set(key, {
              classes: value.classes,
              teacher: value.teacher
                ? {
                    ...value.teacher,
                    full_name: `${value.teacher.full_name} (${teacherSchool?.school?.name})`,
                  }
                : null,
            });
          }
        });

        setTeacherClasses(newTeacherClasses);
      } catch (e) {
        logger.error(e);
      }
    };
    getTeachers();
  }, [schoolId, props.currentUser.role, props.open, selectedUser]);

  useEffect(() => {
    if (!props.open) {
      return;
    }
    if (role === Role.student) {
      if (studentClasses.length === 0) {
        const newStudentClasses: StudentClassRelation[] = [
          {
            teacherId: null,
            classId: null,
          },
        ];
        setStudentClasses(newStudentClasses);
      }
    }
    if (
      role === Role.super &&
      studentClasses.length > 0 &&
      studentClasses[0].classId !== null
    ) {
      setStudentClasses([
        {
          teacherId: null,
          classId: null,
        },
      ]);
      setSchoolId(null);
    }
    if (role !== Role.student) {
      setGrade(null);
    }
  }, [role, props.open, studentClasses]);

  useEffect(() => {
    setIsReadOnlyAdmin(
      selectedUser?.user_subrole?.some(
        (role) => role.sub_role === SubRoles.ADMIN_READ_ONLY
      ) ?? false
    );
  }, [selectedUser]);

  const handleChangeStudentTeacher = (index: number, value: string) => {
    const newStudentClasses = [...studentClasses];
    newStudentClasses[index].teacherId = value;
    setStudentClasses(newStudentClasses);
  };

  const handleChangeStudentClass = (index: number, value: number) => {
    const newStudentClasses = [...studentClasses];
    newStudentClasses[index].classId = value;
    setStudentClasses(newStudentClasses);
  };

  const handleAddStudentClass = () => {
    const newStudentClasses: StudentClassRelation[] = [
      ...studentClasses,
      {
        teacherId: null,
        classId: null,
      },
    ];
    setStudentClasses(newStudentClasses);
  };

  const handleDeleteStudentClass = (index: number) => {
    const newStudentClasses = studentClasses.filter((_, i) => i !== index);
    setStudentClasses(newStudentClasses);
  };

  useEffect(() => {
    if (!props.open) return;
    if (props.currentUser.role === Role.admin) {
      props.currentUser.user_school.forEach((school) => {
        if (school.school) {
          setSchoolList([school.school]);
        }
      });
    }
    if (props.currentUser.role !== Role.super) return;
    const getSchools = async () => {
      try {
        const res = await axios.get<School[]>("/schools");
        setSchoolList(res.data);
      } catch (e) {
        logger.error(e);
      }
    };
    getSchools();
  }, [props.currentUser.role, props.currentUser.user_school, props.open]);

  useEffect(() => {
    if (props.currentUser.role === Role.admin) {
      if (schoolList.length > 0) {
        console.log("Setting school id", props.currentUser.user_school[0].school?.id);
        setSchoolId(Number(props.currentUser.user_school[0].school?.id));
      }
    }
  }, [props.currentUser.role, props.currentUser.user_school, schoolList]);

  useEffect(() => {
    const getFeatureFlags = async () => {
      try {
        const res = await axios.get<{ featureFlagResult: FeatureFlag[] }>(
          "/get-feature-flags"
        );
        setFeatureFlagList(res.data.featureFlagResult);
      } catch (e) {
        logger.error(e);
      }
    };
    getFeatureFlags();
  }, [props.currentUser]);

  useEffect(() => {
    const getSelectedUser = async (userId: string) => {
      try {
        const result = await axios.get(`/user/${userId}`);
        if (result.data && result.data.id) {
          const u = result.data as DbUser;
          setSelectedUser(u);
          // update input fields
          setFirstName(u.first_name);
          setLastName(u.last_name);
          setEmail(u.email);
          setRole(u.role);
          setSchoolId(
            u.user_school && u.user_school.length > 0
              ? Number(u.user_school[0].school_id)
              : null
          );
          setGrade(u.grade ? Number(u.grade) : null);
          setIsTester(u.is_tester);
          setAccessibility(u.accessibility);
          console.log(u.user_feature_flags);
          setFeatureIds(
            u.user_feature_flags
              ?.map((item) => item.feature_flag_id)
              .sort((a, b) => a - b)
              .map((id) => id.toString()) ?? []
          );
          // pre-populate the class/student dropdowns with the selected items
          // handles for classes that don't appear at the student's school in case of LTI mismatch
          const newClassRel: StudentClassRelation[] = [];
          u.class_student?.forEach((item) => {
            const teacherId = item.class?.user?.id ?? "no-teacher-assigned";
            newClassRel.push({
              teacherId: teacherId,
              classId: item.class_id,
            });
          });
          setStudentClasses(newClassRel);
        }
      } catch (e) {
        logger.error(e);
        throw e;
      }
    };
    if (props.user) {
      // Get current user if one selected from Management Page
      if (props.open) getSelectedUser(props.user.id);
    } else {
      if (props.open) {
        clearState();
      }
    }
  }, [props.user, props.open]);

  // course ID, class IDs
  // const selectedCourseIds = useMemo<Map<number, number[]>>(() => {
  //   const courseIdMap = new Map<number, number[]>();
  //   Array.from(teacherClasses).forEach(([key, value]) => {
  //     value.classes.forEach((item) => {
  //       const courseId = item.course_id ?? 1; // default ot pre-alg if course_id is null
  //       const curMapCourseIds = courseIdMap.get(courseId);
  //       const selectedClassIds = studentClasses.map((item) => item.classId);
  //       if (selectedClassIds.includes(item.id)) {
  //         if (!curMapCourseIds || curMapCourseIds.length < 1) {
  //           courseIdMap.set(courseId, [item.id]);
  //         } else {
  //           courseIdMap.set(courseId, [...curMapCourseIds, item.id]);
  //         }
  //       }
  //     });
  //   });
  //   return courseIdMap;
  // }, [teacherClasses, studentClasses]);

  const selectedCourseIds = useMemo<Map<number, number>>(() => {
    const courseIdMap = new Map<number, number>();
    studentClasses.forEach((studentClass, index) => {
      const teachersClasses =
        teacherClasses.get(studentClass.teacherId ?? "no-teacher-assigned")?.classes ??
        [];
      const selectedClass = teachersClasses.find(
        (item) => item.id === studentClass.classId
      );
      if (studentClass.classId !== null) {
        courseIdMap.set(index, selectedClass?.course_id ?? 1);
      }
    });
    return courseIdMap;
  }, [teacherClasses, studentClasses]);

  const formModified = () => {
    const u = props.user;

    const errors = getError();
    if (errors) return false;

    if (u) {
      // Prevent saving the form while selectedUser is populating
      if (!selectedUser || u.id !== selectedUser.id) return false;

      //
      const checkClassIds =
        selectedUser?.class_student?.map((item) => item.class_id) ?? [];
      const selectedClassIds = studentClasses.map((item) => item.classId);
      let classEnrollmentSame = checkClassIds.length === selectedClassIds.length;
      selectedClassIds.forEach((classId) => {
        if (!checkClassIds.includes(classId ?? -1)) {
          classEnrollmentSame = false;
        }
      });

      const originalIsReadOnlyAdmin =
        selectedUser?.user_subrole?.some(
          (role) => role.sub_role === SubRoles.ADMIN_READ_ONLY
        ) ?? false;

      return !(
        firstName === u.first_name &&
        lastName === u.last_name &&
        email === u.email &&
        role === u.role &&
        String(schoolId ?? "") === String(u.school_id ?? "") &&
        classEnrollmentSame &&
        Number(grade) === Number(u.grade) &&
        accessibility === u.accessibility &&
        isTester === u.is_tester &&
        featureIds.join(",") === u.feature_ids &&
        isReadOnlyAdmin === originalIsReadOnlyAdmin
      );
    } else {
      return true;
    }
  };

  const handleClose = () => {
    props.onClose();
  };

  const clearState = () => {
    setSelectedUser(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setRole(Role.student);
    setSchoolId(null);
    setGrade(null);
    setIsTester(false);
    setAccessibility(false);
    setStudentClasses([]);
    setFeatureIds([]);
    setStudentClasses([
      {
        teacherId: null,
        classId: null,
      },
    ]);
  };

  const submit = async () => {
    if (!role) {
      // this shouldn't happen because it's required
      // in the form but it satisfies TS.
      throw Error("no role chosen");
    }
    let data;
    if (props.currentUser.role === Role.teacher) {
      data = {
        accessibility: accessibility,
      };
    } else {
      data = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        grade: grade,
        role: role,
        admin_read_only: isReadOnlyAdmin,
        school_id: schoolId,
        accessibility: accessibility,
        ...(props.currentUser.role === Role.super && {
          is_tester: isTester,
          feature_ids: featureIds,
          clear_feature_ids: featureIds.length < 1,
        }),
        ...(role === Role.student && {
          class_ids: studentClasses.map((item) => item.classId),
        }),
      };
    }

    if (props.user) {
      // edit existing user
      try {
        await axios.put(`/user/${props.user.id}`, data);
        // HandleDataLayer(
        //   "form",
        //   "adding a user",
        //   "onsubmit",
        //   "submit",
        //   "modal",
        //   "edit single user",
        //   "user added",
        //   {
        //     ...data,
        //     teacher_name: `${selectedTeacher ? selectedTeacher.full_name : ""}`,
        //     class_name: `${selectedClass ? selectedClass.subject : ""} ${
        //       selectedClass ? selectedClass.period_block : ""
        //     }`,
        //     school_name: `${selectedSchool ? selectedSchool.name : ""}`,
        //   }
        // );
        props.onSubmit({
          severity: "success",
          message: `Edited ${role} user: ${email}`,
        });
      } catch (e: any) {
        props.onSubmit({
          severity: "error",
          message: (
            <>
              We are not able to edit {email} at this time. Please try again later or
              contact <DigitSupportLink /> for immediate assistance.
            </>
          ),
        });
        // UserManagementModal should handle this error.
        throw e;
      }
    } else {
      // create new user
      // Calling Create cognito user API. and with the received user_id calling the update user API
      try {
        await axios.post<CreateUserRequestBody>("/cognito-users", data);
        // HandleDataLayer(
        //   "form",
        //   "adding a user",
        //   "onsubmit",
        //   "submit",
        //   "modal",
        //   "add single user",
        //   "user added",
        //   {
        //     ...data,
        //     teacher_name: `${selectedTeacher ? selectedTeacher.full_name : ""}`,
        //     class_name: `${selectedClass ? selectedClass.subject : ""} ${
        //       selectedClass ? selectedClass.period_block : ""
        //     }`,
        //     school_name: `${selectedSchool ? selectedSchool.name : ""}`,
        //   }
        // );

        props.onSubmit({
          severity: "success",
          message: `Created ${role} user: ${email}`,
        });
        clearState();
      } catch (e: any) {
        if (
          e instanceof AxiosError &&
          e.response?.data?.message &&
          e.response?.data.message === "UserExists"
        ) {
          props.onSubmit({
            severity: "error",
            message: `${email} already exists. Please use another email address.`,
          });
        } else {
          props.onSubmit({
            severity: "error",
            message: TechnicalIssuesMessage,
          });
        }
        // UserManagementModal should handle this error.
        throw e;
      }
    }
  };

  const HandleDataLayer = (
    event: string,
    action: string,
    name: string,
    type: string,
    region: string,
    section: string,
    text: string,
    metaData?: any
  ) => {
    generateGtmEvent({ event, action, name, type, region, section, text }, metaData);
  };

  const getError = (): string | undefined => {
    if (schoolList.length === 0) {
      return "There are no schools to select. Please contact your administrator to add the school.";
    }
    if (
      [Role.admin, Role.teacher].includes(props.currentUser.role) &&
      !props.currentUser.user_school.length
    ) {
      return "You have not been assigned to a school. Please contact your administrator to assign you to your school.";
    }
    if (schoolId && role === Role.student && teacherClasses.size === 0) {
      return "You do not have any associated teachers for this school. Teachers are required for class selection. Please first create a teacher user.";
    }
    if ([Role.student, Role.admin, Role.teacher].includes(role) && !schoolId) {
      return "Please select a school.";
    }
    if (role === Role.student) {
      const classIds = studentClasses.map((item) => item.classId);
      const classIdsNoNulls = studentClasses
        .map((item) => item.classId)
        .filter((item) => item !== null);
      if (classIds.length < 1) return "Students require at least one class enrollment.";
      if (classIds.length !== classIdsNoNulls.length)
        return "Please select a class for all class enrollments.";
    }

    // if (
    //   state.form.schoolId &&
    //   state.form.teacherId &&
    //   state.form.role === Role.student &&
    //   state.data.classes.succeeded &&
    //   state.data.classes.value.length === 0
    // ) {
    //   if (state.currentUser.role === Role.teacher) {
    //     return 'You do not have any associated classes for students to enroll.  Please first create a class in the "Add Class" form. A class is required to add a student.';
    //   } else {
    //     return 'The selected teacher does not have any associated classes for students to enroll. Please first create a class for this teacher in the "Add Class" form. A class is required to add a student.';
    //   }
    // }
  };

  const handleOpenConfirmationModal = (index: number, classes?: Class[]) => {
    const classId = studentClasses[index].classId;
    const classDetails = classes?.find((item) => item.id === classId);
    setPromptOpen({
      index: index,
      className: `${classDetails?.subject} ${classDetails?.period_block}` ?? "",
    });
  };
  const handleConfirmCancel = () => {
    setPromptOpen(null);
  };
  const handleConfirmRemove = () => {
    if (promptOpen !== null) {
      handleDeleteStudentClass(promptOpen.index);
    }
    setPromptOpen(null);
  };

  return (
    <UserManagementModal
      title={props.user ? "Edit user" : "Add single user"}
      open={props.open}
      onClose={handleClose}
      onSave={submit}
      disableSubmit={!formModified()}
      errorText={firstName && lastName && email && getError()}>
      <Typography
        variant="h6"
        sx={{
          marginBottom: "16px",
          color: "#000000",
          fontWeight: "bold",
          fontSize: "24px",
        }}>
        Profile
      </Typography>
      <Grid container spacing={6}>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            label="First Name"
            fullWidth
            disabled={display}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            label="Last Name"
            fullWidth
            disabled={display}
            required
          />
        </Grid>
        {displayEmail && (
          <Grid item xs={12}>
            <TextField
              className={classes.inputText}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label="Email"
              type="email"
              fullWidth
              disabled={props.user ? true : false} // email field is used by gradarius as the unique identifier. cannot change
              required
            />
          </Grid>
        )}
        <Grid item xs={4}>
          <RoleSelect
            value={role ?? Role.student}
            onChange={(role) => setRole(role)}
            currentUserRole={props.currentUser.role}
            disabled={display}
          />
        </Grid>
        <Grid item xs={4}>
          <SchoolSelect
            schoolId={`${schoolId}`}
            onChange={(schoolId) => setSchoolId(Number(schoolId))}
            options={schoolList}
            required={role !== Role.super}
            disabled={
              display || props.currentUser.role !== Role.super || role === Role.super
            }
          />
        </Grid>
        <Grid item xs={4}>
          <GradeSelect
            grade={grade ?? 0}
            onChange={(grade) => setGrade(grade === 0 ? null : grade)}
            disabled={display || role !== Role.student}
            required={role === Role.student}
          />
        </Grid>
      </Grid>
      {displaySubRole && (
        <Box sx={{ marginTop: "24px" }}>
          <CustomCheckBox
            label={
              "Please check this box if the user is being added to test the platform."
            }
            tooltip={
              props.user?.is_tester_class
                ? "This student's class is flagged as a test class"
                : undefined
            }
            checked={isTester}
            indeterminate={props.user?.is_tester_class && !isTester}
            onChange={(e) => setIsTester(e.target.checked)}
            emitDataLayer={(checked) => {
              HandleDataLayer(
                "form",
                checked ? "click" : "unclick",
                "onclick",
                "checkbox",
                "modal",
                "user interface testing",
                "please check this box if user is being added to test this platform"
              );
            }}
          />
        </Box>
      )}
      {displaySubRole && role === Role.admin && (
        <Box sx={{ marginTop: "24px" }}>
          <CustomCheckBox
            label={"View Only Access"}
            checked={isReadOnlyAdmin}
            onChange={(e) => setIsReadOnlyAdmin(e.target.checked)}
            emitDataLayer={(checked) => {
              HandleDataLayer(
                "form",
                checked ? "click" : "unclick",
                "onclick",
                "checkbox",
                "modal",
                "user interface testing",
                "view only access"
              );
            }}
          />
        </Box>
      )}
      {role === Role.student && (
        <>
          <Typography
            variant="h6"
            sx={{
              marginTop: "24px",
              marginBottom: "16px",
              color: "#000000",
              fontWeight: "bold",
              fontSize: "24px",
            }}>
            Classes
          </Typography>
          <Stack direction="column" gap={6}>
            {studentClasses.map((studentClass, index) => {
              const curTeacherClasses: Class[] =
                teacherClasses.get(studentClass.teacherId ?? "no-teacher-assigned")
                  ?.classes ?? [];

              const otherFieldsSelectedCourses: number[] = [];
              Array.from(selectedCourseIds).forEach(([key, value]) => {
                if (key !== index) {
                  otherFieldsSelectedCourses.push(value);
                }
              });
              const availableClassOptions = curTeacherClasses
                .filter(
                  (item) =>
                    !otherFieldsSelectedCourses.includes(item.course_id ?? 1) ||
                    studentClass.classId === item.id
                )
                .sort((a, b) => {
                  const subjectA = a.subject?.toLowerCase() ?? "";
                  const subjectB = b.subject?.toLowerCase() ?? "";
                  return subjectA.localeCompare(subjectB);
                });

              return (
                <>
                  {index > 0 && <Divider sx={{ borderBottomWidth: "1px" }} />}
                  <Stack direction="row" alignItems="center">
                    <Box sx={{ flexGrow: 1 }}>
                      <Grid container spacing={6}>
                        <Grid item xs={6}>
                          <TeacherSelect
                            teacherId={studentClass.teacherId ?? ""}
                            onChange={(teacherId) =>
                              handleChangeStudentTeacher(index, teacherId)
                            }
                            disabled={
                              display ||
                              props.currentUser.role === Role.teacher ||
                              role !== Role.student ||
                              !schoolId
                            }
                            required={role === Role.student}
                            options={
                              (Array.from(teacherClasses)
                                .map(([key, value]) => value.teacher)
                                .filter((item) => item !== null) as DbUser[]) ?? []
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <ClassSelect
                            classId={`${studentClass.classId ?? ""}`}
                            onChange={(classId) =>
                              handleChangeStudentClass(index, Number(classId))
                            }
                            options={availableClassOptions}
                            disabled={
                              display ||
                              role !== Role.student ||
                              !schoolId ||
                              !studentClass.teacherId
                            }
                            required={role === Role.student}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <IconButton
                      onClick={() => {
                        if (props.user && studentClass.classId !== null) {
                          handleOpenConfirmationModal(
                            index,
                            teacherClasses.get(
                              studentClass.teacherId ?? "no-teacher-assigned"
                            )?.classes
                          );
                        } else {
                          handleDeleteStudentClass(index);
                        }
                      }}
                      aria-label="close edit user account"
                      className={classes.deleteClassRelButton}
                      disabled={studentClasses.length <= 1}>
                      <i className="fa-regular fa-trash"></i>
                    </IconButton>
                  </Stack>
                </>
              );
            })}
            <Box>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                className={classes.addCourseButton}
                onClick={() => handleAddStudentClass()}>
                <i className="fa-regular fa-plus" style={{ marginRight: "4px" }}></i> Add
                course
              </Button>
            </Box>
          </Stack>
        </>
      )}

      <Typography
        variant="h6"
        sx={{
          marginTop: "24px",
          color: "#000000",
          fontWeight: "bold",
          fontSize: "24px",
        }}>
        Accessibility
      </Typography>
      <Typography variant={"body1"}>
        By turning this on, it will make the problem solving platform more accessible by
        enabling keyboard functionality and higher contrast.
      </Typography>
      <CustomCheckBox
        label={"Enhance accessibility"}
        checked={accessibility}
        onChange={(e) => setAccessibility(e.target.checked)}
        emitDataLayer={(checked) => {
          HandleDataLayer(
            "form",
            checked ? "click" : "unclick",
            "onclick",
            "checkbox",
            "modal",
            "accessibility",
            "enhance accessibility"
          );
        }}
      />
      {displaySubRole && (
        <>
          <Typography
            variant="h6"
            sx={{
              marginTop: "24px",
              color: "#000000",
              fontWeight: "bold",
              fontSize: "24px",
            }}>
            Feature Flags
          </Typography>
          <Typography variant={"body1"}>
            Enable select experimental features for this user by specifying their feature
            flags.
          </Typography>
          <FeatureFlagSelect
            featureIds={featureIds}
            onChange={(featureIds) => setFeatureIds(featureIds)}
            options={featureFlagList}
            disabled={false}
          />
        </>
      )}
      <Dialog
        fullWidth
        maxWidth="sm"
        classes={{
          paper: classes.newPosOfDialog,
        }}
        onClose={handleConfirmCancel}
        aria-labelledby="simple-dialog-title"
        open={promptOpen !== null}>
        <Typography variant="body1" className={classes.dialogText}>
          Remove {props.user?.full_name} from{" "}
          {promptOpen !== null ? promptOpen.className : "this class"}?
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="info"
            size="large"
            className={classes.buttonNo}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemove}
            variant="contained"
            color="secondary"
            size="large"
            className={classes.buttonYes}>
            Remove
          </Button>
        </Stack>
      </Dialog>
    </UserManagementModal>
  );
}
